import { jsx, jsxs } from "react/jsx-runtime";
import React__default from "react";
import { isCallState, isOutputState } from "./types.js";
import { GREY_PLACEHOLDER_SVG } from "./utils.js";
import { getRetailerFromId } from "../../../utils/stringUtils.js";
import { getDefaultProductImage, getFallbackProductImage } from "../../../utils/assetUtils.js";
import { getAppConsistentParams } from "../../../utils/appUtils.js";
import { getProductUrl } from "../../../utils/productUtils.js";
const STAR_PATH = "M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z";
function StarIcon({ fillType }) {
  if (fillType === "half") {
    return /* @__PURE__ */ jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: [
      /* @__PURE__ */ jsx("defs", { children: /* @__PURE__ */ jsx("clipPath", { id: "halfClip", children: /* @__PURE__ */ jsx("rect", { x: "0", y: "0", width: "12", height: "24" }) }) }),
      /* @__PURE__ */ jsx("path", { d: STAR_PATH, fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }),
      /* @__PURE__ */ jsx("path", { d: STAR_PATH, fill: "currentColor", clipPath: "url(#halfClip)" })
    ] });
  }
  return /* @__PURE__ */ jsx("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: fillType === "full" ? "currentColor" : "none", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx("path", { d: STAR_PATH, stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
function CompareStarRating({ rating, reviewCount }) {
  const stars = Array.from({ length: 5 }, (_, i) => {
    if (rating >= i + 1) return "full";
    if (rating >= i + 0.25) return "half";
    return "empty";
  });
  return /* @__PURE__ */ jsxs("span", { className: "concierge-compare-star-rating", children: [
    stars.map((fill, i) => /* @__PURE__ */ jsx(StarIcon, { fillType: fill }, i)),
    /* @__PURE__ */ jsx("span", { className: "concierge-compare-star-text", children: rating.toFixed(1) }),
    reviewCount != null && /* @__PURE__ */ jsxs("span", { className: "concierge-compare-muted", children: [
      "(",
      reviewCount.toLocaleString(),
      ")"
    ] })
  ] });
}
function CompareProductImage({ sku, imageUrl, title }) {
  const fallbacks = React__default.useMemo(() => {
    const seen = /* @__PURE__ */ new Set();
    const urls = [];
    for (const url of [imageUrl, getDefaultProductImage(sku), getFallbackProductImage(sku)]) {
      if (url && !seen.has(url)) {
        seen.add(url);
        urls.push(url);
      }
    }
    urls.push(GREY_PLACEHOLDER_SVG);
    return urls;
  }, [sku, imageUrl]);
  const [idx, setIdx] = React__default.useState(0);
  return /* @__PURE__ */ jsx("div", { className: "concierge-compare-image-container", children: /* @__PURE__ */ jsx(
    "img",
    {
      src: fallbacks[idx],
      alt: title,
      className: "concierge-compare-image",
      onError: () => setIdx((i) => Math.min(i + 1, fallbacks.length - 1))
    }
  ) });
}
function renderAttributeCell(attr, product) {
  switch (attr.type) {
    case "price":
      return product.price != null ? /* @__PURE__ */ jsxs("span", { className: "concierge-compare-price", children: [
        "$",
        Number(product.price).toFixed(2)
      ] }) : "—";
    case "rating":
      return product.rating != null ? /* @__PURE__ */ jsx(CompareStarRating, { rating: Number(product.rating), reviewCount: product.reviewCount }) : "—";
    case "availability":
      return product.isAvailable == null ? "—" : product.isAvailable ? /* @__PURE__ */ jsx("span", { className: "concierge-compare-available", children: "In stock" }) : /* @__PURE__ */ jsx("span", { className: "concierge-compare-unavailable", children: "Out of stock" });
    case "reward":
      return product.hasRewards && product.totalRewardAmount != null ? /* @__PURE__ */ jsxs("span", { className: "concierge-compare-reward", children: [
        "$",
        Number(product.totalRewardAmount).toFixed(2),
        " cashback"
      ] }) : /* @__PURE__ */ jsx("span", { className: "concierge-compare-muted", children: "None" });
    case "bullets": {
      const bullets = product[attr.key];
      return (bullets == null ? void 0 : bullets.length) > 0 ? /* @__PURE__ */ jsx("ul", { className: "concierge-compare-bullets", children: bullets.map((b, i) => /* @__PURE__ */ jsx("li", { children: b }, i)) }) : "—";
    }
    default: {
      const value = product[attr.key];
      if (value == null || value === "") return "—";
      if (attr.key === "retailerId") return getRetailerFromId(value) || "—";
      return String(value);
    }
  }
}
function CompareProductsPart({ part }) {
  var _a, _b;
  if (isCallState(part.state)) {
    return /* @__PURE__ */ jsx("div", { className: "concierge-loading", children: "Comparing products..." });
  }
  if (isOutputState(part.state) && ((_b = (_a = part.output) == null ? void 0 : _a.products) == null ? void 0 : _b.length) >= 2) {
    const products = part.output.products;
    const attributes = part.output.comparisonAttributes ?? [];
    const urlSearchParams = getAppConsistentParams(window.location.search);
    return /* @__PURE__ */ jsx("div", { className: "concierge-compare-wrapper", children: /* @__PURE__ */ jsx("div", { className: "concierge-compare-scroll", children: /* @__PURE__ */ jsxs("table", { className: "concierge-compare-table", children: [
      /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { children: [
        /* @__PURE__ */ jsx("th", { className: "concierge-compare-attr-col" }),
        products.map((p, i) => {
          const targetUrl = getProductUrl({
            title: p.title || "",
            stacklineSku: p.sku,
            urlSearchParams
          });
          return /* @__PURE__ */ jsx("th", { className: "concierge-compare-product-col", children: /* @__PURE__ */ jsxs("div", { className: "concierge-compare-product-link", children: [
            /* @__PURE__ */ jsx("div", { className: "concierge-compare-title", children: p.title }),
            /* @__PURE__ */ jsx(CompareProductImage, { sku: p.sku, imageUrl: p.imageUrl, title: p.title }),
            /* @__PURE__ */ jsx(
              "a",
              {
                href: targetUrl,
                target: "_blank",
                rel: "noopener noreferrer",
                className: "concierge-compare-cta",
                children: "View product"
              }
            )
          ] }) }, p.sku || i);
        })
      ] }) }),
      /* @__PURE__ */ jsx("tbody", { children: attributes.map((attr, rowIdx) => /* @__PURE__ */ jsxs("tr", { className: `concierge-compare-row${rowIdx % 2 === 0 ? " concierge-compare-row-even" : ""}`, children: [
        /* @__PURE__ */ jsx("td", { className: "concierge-compare-label", children: attr.label }),
        products.map((p, i) => /* @__PURE__ */ jsx("td", { className: "concierge-compare-cell", children: renderAttributeCell(attr, p) }, p.sku || i))
      ] }, attr.key)) })
    ] }) }) });
  }
  return null;
}
export {
  CompareProductsPart
};
//# sourceMappingURL=CompareProductsPart.js.map
